import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { drawGrid } from "./../../../utils/drawGrid/drawGrid";
import {
  boxHeight,
  canvasWrapperId,
  dayWidth,
  weekWidth,
  leftColumnWidth,
  outsideWrapperId,
  monthWidth,
} from "./../../../constants";
import { Loader, Tiles } from "./../../../components";
import { useCalendar } from "./../../../context/CalendarProvider";
import { resizeCanvas } from "./../../../utils/resizeCanvas";
import { getCanvasWidth } from "./../../../utils/getCanvasWidth";
import { GridProps } from "./types";
import {
  StyledCanvas,
  StyledInnerWrapper,
  StyledSpan,
  StyledWrapper,
} from "./styles";
import dayjs from "dayjs";
import { ModalFormAlocation } from "../../ModalFormAllocation";
import { DataAllocationToApi } from "../../../../@types/dataAllocationToApi.type";
import usePermission from "../../../../hooks/usePermission";
import { FeatureStatic } from "../../../../@types/statics/feature.static";
import { toast } from "react-toastify";
import { getCols } from "../../../utils/getCols";
import { AllocationBlock, PaginatedSchedulerRow } from "../../../types/global";

const Grid = forwardRef<HTMLDivElement, GridProps>(function Grid(
  { zoom, rows, data, onTileClick },
  ref
) {
  const {
    handleScrollNext,
    handleScrollPrev,
    date,
    isLoading,
    cols,
    startDate,
    range,
    listHolidays,
  } = useCalendar();
  const [dataAllocation, setDataAllocation] = useState(
    {} as DataAllocationToApi
  );
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const refRight = useRef<HTMLSpanElement>(null);
  const refLeft = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const permissions = usePermission(FeatureStatic.allocationMap.id);

  const theme = useTheme();

  const isDateBlocked = (date: Date, blocks?: AllocationBlock[]) => {
    if (!blocks?.length) return false;

    const day = dayjs(date);

    return blocks.some(block => {
      const start = dayjs(block.startDate);
      const end = dayjs(block.endDate);

      return day.isSame(start, "day") || day.isSame(end, "day") || (day.isAfter(start, "day") && day.isBefore(end, "day"));
    });
  };


  const handleResize = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const width = getCanvasWidth(zoom, cols);
      const height = rows * boxHeight + 1;
      const colsNew = getCols(zoom, date);

      resizeCanvas(ctx, width, height);
      drawGrid(ctx, zoom, rows, colsNew, startDate, theme, listHolidays, data);
    },
    [cols, startDate, rows, zoom, theme, listHolidays, data, date]
  );

  useEffect(() => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    const onResize = () => handleResize(ctx);

    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, [handleResize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.style.letterSpacing = "1px";
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    handleResize(ctx);
  }, [date, rows, zoom, handleResize]);

  useEffect(() => {
    if (!refRight.current) return;
    const observerRight = new IntersectionObserver(
      (e) => (e[0].isIntersecting ? handleScrollNext() : null),
      { root: document.getElementById(outsideWrapperId) }
    );
    observerRight.observe(refRight.current);

    return () => observerRight.disconnect();
  }, [handleScrollNext]);

  useEffect(() => {
    if (!refLeft.current) return;
    const observerLeft = new IntersectionObserver(
      (e) => (e[0].isIntersecting ? handleScrollPrev() : null),
      {
        root: document.getElementById(outsideWrapperId),
        rootMargin: `0px 0px 0px -${leftColumnWidth}px`,
      }
    );
    observerLeft.observe(refLeft.current);

    return () => observerLeft.disconnect();
  }, [handleScrollPrev]);

  const handleClick = async (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Obter a posição do clique dentro do canvas
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const rowHeight = boxHeight; // Altura de cada linha/célula

    const colIndex = Math.floor(
      x / (zoom === 1 ? dayWidth : zoom === 4 ? monthWidth : weekWidth)
    );
    const rowIndex = Math.floor(y / rowHeight);

    // Usando dayjs para lidar com a data
    const startDateDayjs = dayjs(
      new Date(startDate.year, startDate.month, startDate.dayOfMonth)
    );

    // Calcule a nova data com base na célula clicada
    const clickedDate = startDateDayjs
      .add(colIndex, zoom === 1 ? "days" : zoom === 4 ? "months" : "weeks")
      .toDate();

    let clickedRow: PaginatedSchedulerRow | undefined;
    let accumulatedRows = 0;

    for (let row of data) {
      const rowLength = row.data.length > 0 ? row.data.length : 1;
      accumulatedRows += rowLength;

      if (accumulatedRows > rowIndex) {
        clickedRow = row;
        break;
      }
    }

    if (!clickedRow) return;
    const allocationBlocks = clickedRow.allocationBlocks ?? [];
    const isDefaultAvailability = clickedRow.label.isDefaultAvailability ?? true;
    const canAllocation = !isDefaultAvailability ? clickedRow.label.availabilities?.some((item) =>  (item.endTime1 && item.startTime1) || (item.endTime2 && item.startTime2))  ?? false : true ;

    // Verifica permissão
    if (!permissions.canCreate()) {
      toast.error("Você não tem permissão para criar um apontamento");
      return;
    }

    // Verifica se a data está bloqueada pelo allocationBlocks
    if (isDateBlocked(clickedDate, allocationBlocks)) {
      toast.error("Este período está bloqueado para alocação.");
      return;
    }

    // Verifica se há disponibilidade real do colaborador
    if (!canAllocation) {
      toast.error("Colaborador não tem disponibilidade");
      return;
    }

    // Abre modal se passou nas validações
    setDataAllocation({
      ...dataAllocation,
      isProject: true,
      startDate: clickedDate,
      resourceId: clickedRow.id,
      title: clickedRow.label.title,
      subtitle: clickedRow.label.subtitle,
    });
    handleOpen();
  };



  return (
    <>
      <StyledWrapper id={canvasWrapperId}>
        <StyledInnerWrapper ref={ref}>
          <StyledSpan position="left" ref={refLeft} />
          <Loader isLoading={isLoading} position="left" />
          <StyledCanvas ref={canvasRef} onClick={handleClick} />
          <Tiles data={data} zoom={zoom} onTileClick={onTileClick} />
          <StyledSpan ref={refRight} position="right" />
          <Loader isLoading={isLoading} position="right" />
        </StyledInnerWrapper>
      </StyledWrapper>
      <ModalFormAlocation
        dataAllocation={dataAllocation}
        open={open}
        handleClose={handleClose}
      />
    </>
  );
});

export default Grid;
